import { FileItem } from './types';

/**
 * The identity of one file-browser entry — what every affordance keys on.
 *
 * STD-029 R-2: selection, clipboard, drag-drop, context menus, navigation state
 * and share targets act on `object_id`. `FileItem.key` is a *display* datum, and
 * keying UI state on it is the defect this module exists to end:
 *
 *  - a path-keyed selection strands the moment a row is renamed or moved, since
 *    identity, the S3 key and the display path are three independent things
 *    (M-7);
 *  - two rows at one display path — a state `merge_arms` permits, and one the
 *    registry does not forbid — collapse into a single selectable item, so
 *    clicking one highlights both and a bulk action silently hits the wrong row;
 *  - and it erased the `object_id` that folder sharing keys on, which is exactly
 *    how "Share folder…" became unreachable for every folder in the tab (#1324).
 *
 * The `key` fallback exists ONLY for entries served by the `fleet.flights` arm,
 * which carry no registry row and therefore no `object_id` (R-9). Those entries
 * stay selectable, cuttable and draggable — they degrade by *hiding id-keyed
 * affordances* (share, rename, move, delete, verdict badge), never by erroring
 * or vanishing.
 *
 * **The fallback does NOT die with B-4.** An earlier version of this paragraph
 * said it would, on the theory that retiring the flat projection would leave
 * every entry with an id. It does not: `register_uploaded_file` fails soft by
 * design, so a CSV upload whose registry write did not take still leaves a
 * flight with no row, and B-4's listing carries those in their own
 * `unregistered` array precisely so they stay visible. Treating `object_id` as
 * optional is mandatory (R-9), permanently, and not defensive.
 *
 * **Nothing may parse the result.** An entry id is a uuid for registry-backed
 * rows and a path only by accident of the fallback; code that needs a path must
 * read `file.key` off the entry itself. (`moveGuards` is the one path-math
 * module left, and it takes entries, not ids — see its header.)
 *
 * **The "id is not stable across a refetch" limit is CLOSED (B-4).** It existed
 * because folders were synthesised on the client from key segments, so a folder
 * rendered with the path fallback on one listing and with its uuid on the next,
 * once the `name/` marker row that `indexFolderObjectIds` read came through — and
 * a selection made in that window silently dropped. Folder synthesis is gone: a
 * folder is a row the server sends, with an id, on every listing. The remaining
 * fallback applies only to R-9 entries, which have no id on any listing and
 * therefore cannot change identity between two of them.
 */
export function entryId(file: Pick<FileItem, 'object_id' | 'key'>): string {
  return file.object_id ?? file.key;
}

/**
 * The identities of a list of entries, in order.
 *
 * Exists so the mapping lives in the tested layer rather than being spelled out
 * at each call site: `files.map((f) => f.key)` and `files.map(entryId)` are both
 * `string[]`, so the type-checker cannot tell a converted call site from a
 * reverted one, and every consumer of an ordered id list (Shift-range,
 * select-all, the card view models, the post-move selection) would fail
 * silently rather than loudly.
 */
export function entryIds(files: readonly Pick<FileItem, 'object_id' | 'key'>[]): string[] {
  return files.map(entryId);
}
